import { createContext, useContext, useState, useCallback, useEffect } from "react";

const WeightContext = createContext({
  weight: "",
  setWeight: () => {},
  clearWeight: () => {},
  hasWeight: false,
  concentrations: {},
  infusionDosing: {},
  drugIndications: {},
  userConcentrationChoices: {},
  setConcentrationChoice: () => {},
});

export const WeightProvider = ({ children }) => {
  const [weight, setWeight] = useState("");
  const [concentrations, setConcentrations] = useState({});
  const [infusionDosing, setInfusionDosing] = useState({});
  const [allowedServices, setAllowedServices] = useState({});
  const [drugIndications, setDrugIndications] = useState({});
  const [userConcentrationChoices, setUserConcentrationChoices] = useState(() => {
    try { return JSON.parse(localStorage.getItem("yems_conc_choices") || "{}"); } catch { return {}; }
  });

  const clearWeight = useCallback(() => {
    setWeight("");
  }, []);

  const hasWeight = weight && parseFloat(weight) > 0;

  const setConcentrationChoice = useCallback((drug, optionIndex) => {
    setUserConcentrationChoices(prev => {
      const next = { ...prev, [drug]: optionIndex };
      localStorage.setItem("yems_conc_choices", JSON.stringify(next));
      return next;
    });
  }, []);

  // Fetch concentrations from API
  useEffect(() => {
    const API = process.env.REACT_APP_BACKEND_URL || "";
    fetch(`${API}/api/drug-concentrations`)
      .then(r => r.json())
      .then(data => {
        const map = {};
        const dosingMap = {};
        const allowedMap = {};
        const indicationsMap = {};
        for (const d of data) {
          map[d.drug] = d.options;
          if (d.infusion_dosing) dosingMap[d.drug] = d.infusion_dosing;
          if (d.allowed_services) allowedMap[d.drug] = d.allowed_services;
          if (d.indications) indicationsMap[d.drug] = d.indications;
        }
        setConcentrations(map);
        setInfusionDosing(dosingMap);
        setAllowedServices(allowedMap);
        setDrugIndications(indicationsMap);
      })
      .catch(() => {});
  }, []);

  return (
    <WeightContext.Provider value={{ weight, setWeight, clearWeight, hasWeight, concentrations, infusionDosing, allowedServices, drugIndications, userConcentrationChoices, setConcentrationChoice }}>
      {children}
    </WeightContext.Provider>
  );
};

export const useWeight = () => {
  const context = useContext(WeightContext);
  if (!context) {
    throw new Error("useWeight must be used within WeightProvider");
  }
  return context;
};

// Extract max dose limits from text (e.g., "MAX 10mg", "40mg max", "max single dose 10mg")
const extractMaxDoses = (text) => {
  const maxes = [];
  let m;
  // "MAX 10mg", "max 300mg", "MAX single dose 10mg", "maximum 50mg"
  const p1 = /max(?:imum)?(?:\s+single\s+dose)?\s+(\d+(?:\.\d+)?)\s*(mg|mcg|ml|cc|g|J)\b/gi;
  while ((m = p1.exec(text)) !== null) {
    maxes.push({ value: parseFloat(m[1]), unit: m[2].toLowerCase() });
  }
  // "40mg max", "500ml Max"
  const p2 = /(\d+(?:\.\d+)?)\s*(mg|mcg|ml|cc|g|J)\s+max\b/gi;
  while ((m = p2.exec(text)) !== null) {
    maxes.push({ value: parseFloat(m[1]), unit: m[2].toLowerCase() });
  }
  return maxes;
};

// Apply max dose cap to a result
const applyMaxCap = (result, maxDoses) => {
  const baseUnit = result.unit.toLowerCase().replace('/min', '');
  const cap = maxDoses.find(m => m.unit === baseUnit || (baseUnit === 'cc' && m.unit === 'ml') || (baseUnit === 'ml' && m.unit === 'cc'));
  if (!cap) return result;

  const r = { ...result };
  const decimals = baseUnit === 'ml' || baseUnit === 'cc' || baseUnit === 'j' ? 0 : 2;
  if (r.value) {
    const v = parseFloat(r.value);
    if (v > cap.value) { r.value = cap.value % 1 === 0 ? String(cap.value) : cap.value.toFixed(decimals); r.capped = true; }
  }
  if (r.max) {
    const v = parseFloat(r.max);
    if (v > cap.value) { r.max = cap.value % 1 === 0 ? String(cap.value) : cap.value.toFixed(decimals); r.capped = true; }
  }
  if (r.min) {
    const v = parseFloat(r.min);
    if (v > cap.value) { r.min = cap.value % 1 === 0 ? String(cap.value) : cap.value.toFixed(decimals); r.capped = true; }
  }
  if (r.capped) r.maxDose = `${cap.value}${cap.unit}`;
  return r;
};

// Drug concentration map (mg/mL or mcg/mL) for volume calculations
// Based on YEMS drug monograph vial concentrations
const DRUG_CONCENTRATIONS = {
  // name patterns (lowercase) -> { conc: number, unit: 'mg'|'mcg', perMl: true }
  'epinephrine': [
    { conc: 1, unit: 'mg', label: '1:1000' },       // 1 mg/mL IM
  ],
  'adenosine': [{ conc: 3, unit: 'mg', label: '3mg/mL' }],
  'amiodarone': [{ conc: 50, unit: 'mg', label: '50mg/mL' }],
  'diazepam': [{ conc: 5, unit: 'mg', label: '5mg/mL' }],
  'valium': [{ conc: 5, unit: 'mg', label: '5mg/mL' }],
  'diphenhydramine': [{ conc: 50, unit: 'mg', label: '50mg/mL' }],
  'benadryl': [{ conc: 50, unit: 'mg', label: '50mg/mL' }],
  'ephedrine': [{ conc: 50, unit: 'mg', label: '50mg/mL' }],
  'fentanyl': [{ conc: 50, unit: 'mcg', label: '50mcg/mL' }],
  'haloperidol': [{ conc: 5, unit: 'mg', label: '5mg/mL' }],
  'haldol': [{ conc: 5, unit: 'mg', label: '5mg/mL' }],
  'ketamine': [{ conc: 50, unit: 'mg', label: '50mg/mL' }],
  'ketalar': [{ conc: 50, unit: 'mg', label: '50mg/mL' }],
  'ketorolac': [{ conc: 30, unit: 'mg', label: '30mg/mL' }],
  'toradol': [{ conc: 30, unit: 'mg', label: '30mg/mL' }],
  'lidocaine': [{ conc: 20, unit: 'mg', label: '20mg/mL' }],
  'midazolam': [{ conc: 5, unit: 'mg', label: '5mg/mL' }],
  'versed': [{ conc: 5, unit: 'mg', label: '5mg/mL' }],
  'morphine': [{ conc: 10, unit: 'mg', label: '10mg/mL' }],
  'naloxone': [{ conc: 1, unit: 'mg', label: '1mg/mL' }],
  'narcan': [{ conc: 1, unit: 'mg', label: '1mg/mL' }],
  'norepinephrine': [{ conc: 1, unit: 'mg', label: '1mg/mL' }],
  'levophed': [{ conc: 1, unit: 'mg', label: '1mg/mL' }],
  'ondansetron': [{ conc: 2, unit: 'mg', label: '2mg/mL' }],
  'zofran': [{ conc: 2, unit: 'mg', label: '2mg/mL' }],
  'succinylcholine': [{ conc: 20, unit: 'mg', label: '20mg/mL' }],
  'rocuronium': [{ conc: 10, unit: 'mg', label: '10mg/mL' }],
  'zemuron': [{ conc: 10, unit: 'mg', label: '10mg/mL' }],
  'txa': [{ conc: 100, unit: 'mg', label: '100mg/mL' }],
  'tranexamic': [{ conc: 100, unit: 'mg', label: '100mg/mL' }],
  'dexamethasone': [{ conc: 4, unit: 'mg', label: '4mg/mL' }],
  'magnesium': [{ conc: 500, unit: 'mg', label: '500mg/mL' }],
  'dimenhydrinate': [{ conc: 50, unit: 'mg', label: '50mg/mL' }],
  'gravol': [{ conc: 50, unit: 'mg', label: '50mg/mL' }],
  'propofol': [{ conc: 10, unit: 'mg', label: '10mg/mL' }],
  'glucagon': [{ conc: 1, unit: 'mg', label: '1mg/mL' }],
  'calcium gluconate': [{ conc: 100, unit: 'mg', label: '100mg/mL' }],
  'lorazepam': [{ conc: 4, unit: 'mg', label: '4mg/mL' }],
  'atropine': [{ conc: 0.4, unit: 'mg', label: '0.4mg/mL' }],
};

// Find drug concentration for a given text line — uses API-loaded concentrations
const findDrugConcentration = (text, doseUnit, concentrations, userChoices) => {
  if (!concentrations || Object.keys(concentrations).length === 0) {
    return findDrugConcentrationFallback(text, doseUnit);
  }
  const lower = text.toLowerCase();
  for (const [drug, options] of Object.entries(concentrations)) {
    if (lower.includes(drug)) {
      // User has chosen a specific concentration
      const choiceIdx = userChoices?.[drug];
      if (choiceIdx !== undefined && options[choiceIdx] && options[choiceIdx].unit === doseUnit) {
        return { ...options[choiceIdx], drug };
      }
      // Find default
      const def = options.find(o => o.default && o.unit === doseUnit);
      if (def) return { ...def, drug };
      // Fallback to first matching unit
      const match = options.find(o => o.unit === doseUnit);
      if (match) return { ...match, drug };
    }
  }
  return null;
};

// Fallback to hardcoded concentrations (for offline/initial load)
const findDrugConcentrationFallback = (text, doseUnit) => {
  const lower = text.toLowerCase();
  for (const [drug, concs] of Object.entries(DRUG_CONCENTRATIONS)) {
    if (lower.includes(drug)) {
      const match = concs.find(c => c.unit === doseUnit) || concs[0];
      if (match.unit === doseUnit) return { ...match, drug };
    }
  }
  return null;
};

// Calculate volume from dose and concentration
const calcVolume = (doseMg, concentration) => {
  if (!concentration || !doseMg || doseMg <= 0) return null;
  const vol = doseMg / concentration.conc;
  // Format nicely
  if (vol < 0.1) return vol.toFixed(2);
  if (vol < 1) return vol.toFixed(1);
  return vol.toFixed(1);
};

// Utility function to parse and calculate weight-based doses
export const parseWeightBasedDose = (text, weightKg, concentrations, userChoices) => {
  if (!text || !weightKg || weightKg <= 0) return null;
  
  const results = [];
  const weight = parseFloat(weightKg);
  const maxDoses = extractMaxDoses(text);
  
  // Pattern for mg/kg doses (e.g., "0.1mg/kg", "0.05-0.1mg/kg", "1-2mg/kg")
  const mgKgPattern = /(\d+(?:\.\d+)?(?:\s*-\s*\d+(?:\.\d+)?)?)\s*mg\/kg/gi;
  let match;
  while ((match = mgKgPattern.exec(text)) !== null) {
    const doseRange = match[1];
    const conc = findDrugConcentration(text, 'mg', concentrations, userChoices);
    if (doseRange.includes('-')) {
      const [min, max] = doseRange.split('-').map(d => parseFloat(d.trim()));
      const minDose = min * weight;
      const maxDose = max * weight;
      const r = { type: 'mg', min: minDose.toFixed(2), max: maxDose.toFixed(2), original: match[0], unit: 'mg' };
      if (conc) { r.volMin = calcVolume(minDose, conc); r.volMax = calcVolume(maxDose, conc); r.concLabel = conc.label; r.drug = conc.drug; }
      results.push(r);
    } else {
      const dose = parseFloat(doseRange);
      const totalDose = dose * weight;
      const r = { type: 'mg', value: totalDose.toFixed(2), original: match[0], unit: 'mg' };
      if (conc) { r.volume = calcVolume(totalDose, conc); r.concLabel = conc.label; r.drug = conc.drug; }
      results.push(r);
    }
  }
  
  // Pattern for mcg/kg doses (e.g., "1mcg/kg", "0.01-1mcg/kg/min")
  const mcgKgPattern = /(\d+(?:\.\d+)?(?:\s*-\s*\d+(?:\.\d+)?)?)\s*mcg\/kg(?:\/min)?/gi;
  while ((match = mcgKgPattern.exec(text)) !== null) {
    const doseRange = match[1];
    const isPerMin = match[0].toLowerCase().includes('/min');
    const conc = findDrugConcentration(text, 'mcg', concentrations, userChoices);
    if (doseRange.includes('-')) {
      const [min, max] = doseRange.split('-').map(d => parseFloat(d.trim()));
      const minDose = min * weight;
      const maxDose = max * weight;
      const r = { type: 'mcg', min: minDose.toFixed(2), max: maxDose.toFixed(2), original: match[0], unit: isPerMin ? 'mcg/min' : 'mcg' };
      if (conc && !isPerMin) { r.volMin = calcVolume(minDose, conc); r.volMax = calcVolume(maxDose, conc); r.concLabel = conc.label; r.drug = conc.drug; }
      results.push(r);
    } else {
      const dose = parseFloat(doseRange);
      const totalDose = dose * weight;
      const r = { type: 'mcg', value: totalDose.toFixed(2), original: match[0], unit: isPerMin ? 'mcg/min' : 'mcg' };
      if (conc && !isPerMin) { r.volume = calcVolume(totalDose, conc); r.concLabel = conc.label; r.drug = conc.drug; }
      results.push(r);
    }
  }
  
  // Pattern for ml/kg doses (e.g., "20ml/kg", "10ml/kg")
  const mlKgPattern = /(\d+(?:\.\d+)?(?:\s*-\s*\d+(?:\.\d+)?)?)\s*(?:ml|cc)\/kg/gi;
  while ((match = mlKgPattern.exec(text)) !== null) {
    const doseRange = match[1];
    if (doseRange.includes('-')) {
      const [min, max] = doseRange.split('-').map(d => parseFloat(d.trim()));
      results.push({
        type: 'ml',
        min: (min * weight).toFixed(0),
        max: (max * weight).toFixed(0),
        original: match[0],
        unit: 'ml'
      });
    } else {
      const dose = parseFloat(doseRange);
      results.push({
        type: 'ml',
        value: (dose * weight).toFixed(0),
        original: match[0],
        unit: 'ml'
      });
    }
  }
  
  // Pattern for J/kg (joules for defibrillation)
  const jKgPattern = /(\d+(?:\.\d+)?(?:\s*-\s*\d+(?:\.\d+)?)?)\s*[jJ]\/kg/gi;
  while ((match = jKgPattern.exec(text)) !== null) {
    const doseRange = match[1];
    if (doseRange.includes('-')) {
      const [min, max] = doseRange.split('-').map(d => parseFloat(d.trim()));
      results.push({
        type: 'J',
        min: (min * weight).toFixed(0),
        max: (max * weight).toFixed(0),
        original: match[0],
        unit: 'J'
      });
    } else {
      const dose = parseFloat(doseRange);
      results.push({
        type: 'J',
        value: (dose * weight).toFixed(0),
        original: match[0],
        unit: 'J'
      });
    }
  }
  
  // Apply max dose caps
  const capped = results.map(r => applyMaxCap(r, maxDoses));
  
  return capped.length > 0 ? capped : null;
};

// Check if text contains weight-based dosing
export const hasWeightBasedDosing = (text) => {
  if (!text) return false;
  // Match patterns like mg/kg, mcg/kg, ml/kg, cc/kg, J/kg but NOT fixed ranges like "15-30mg"
  return /\d+(?:\.\d+)?(?:\s*-\s*\d+(?:\.\d+)?)?\s*(?:mg|mcg|ml|cc|[jJ])\/kg/i.test(text);
};

// Parse fixed doses (non-weight-based) and calculate volumes
// e.g. "Fentanyl 25mcg IV" -> { drug: "fentanyl", dose: 25, unit: "mcg", volume: "0.5", concLabel: "50mcg/mL" }
export const parseFixedDoseVolume = (text, concentrations, userChoices) => {
  if (!text) return null;
  // Don't process if it's a weight-based dose (those are handled separately)
  if (hasWeightBasedDosing(text)) return null;

  const results = [];
  const lower = text.toLowerCase();

  // Build a list of drug names to search for
  const drugNames = concentrations && Object.keys(concentrations).length > 0
    ? Object.keys(concentrations)
    : Object.keys(DRUG_CONCENTRATIONS);

  for (const drug of drugNames) {
    if (!lower.includes(drug)) continue;

    // Find fixed mg doses: "15mg", "25mg", "0.5mg", "10-20mg" (but not mg/kg or mg/mL)
    const mgPattern = new RegExp(`(\\d+(?:\\.\\d+)?(?:\\s*-\\s*\\d+(?:\\.\\d+)?)?)\\s*mg(?!\\/kg)(?!\\/m[lL])(?!\\/min)\\b`, 'gi');
    let match;
    while ((match = mgPattern.exec(text)) !== null) {
      const conc = concentrations && Object.keys(concentrations).length > 0
        ? findDrugConcentration(text, 'mg', concentrations, userChoices)
        : findDrugConcentrationFallback(text, 'mg');
      if (!conc) continue;
      const doseStr = match[1];
      if (doseStr.includes('-')) {
        const [min, max] = doseStr.split('-').map(d => parseFloat(d.trim()));
        results.push({
          drug: conc.drug || drug,
          dose: `${min}-${max}`,
          unit: 'mg',
          volMin: calcVolume(min, conc),
          volMax: calcVolume(max, conc),
          concLabel: conc.label,
        });
      } else {
        const dose = parseFloat(doseStr);
        results.push({
          drug: conc.drug || drug,
          dose: `${dose}`,
          unit: 'mg',
          volume: calcVolume(dose, conc),
          concLabel: conc.label,
        });
      }
    }

    // Find fixed mcg doses: "25mcg", "50mcg", "100mcg"
    const mcgPattern = new RegExp(`(\\d+(?:\\.\\d+)?(?:\\s*-\\s*\\d+(?:\\.\\d+)?)?)\\s*mcg(?!\\/kg)(?!\\/m[lL])(?!\\/min)\\b`, 'gi');
    while ((match = mcgPattern.exec(text)) !== null) {
      const conc = concentrations && Object.keys(concentrations).length > 0
        ? findDrugConcentration(text, 'mcg', concentrations, userChoices)
        : findDrugConcentrationFallback(text, 'mcg');
      if (!conc) continue;
      const doseStr = match[1];
      if (doseStr.includes('-')) {
        const [min, max] = doseStr.split('-').map(d => parseFloat(d.trim()));
        results.push({
          drug: conc.drug || drug,
          dose: `${min}-${max}`,
          unit: 'mcg',
          volMin: calcVolume(min, conc),
          volMax: calcVolume(max, conc),
          concLabel: conc.label,
        });
      } else {
        const dose = parseFloat(doseStr);
        results.push({
          drug: conc.drug || drug,
          dose: `${dose}`,
          unit: 'mcg',
          volume: calcVolume(dose, conc),
          concLabel: conc.label,
        });
      }
    }

    if (results.length > 0) break; // Found drug match, stop searching
  }

  return results.length > 0 ? results : null;
};

export default WeightContext;
export { DRUG_CONCENTRATIONS, findDrugConcentration, calcVolume };
